/**
 * tRPC React Query Client
 * Provides end-to-end type-safe API access using tRPC with React Query.
 * @packageDocumentation
 */

import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type { AppRouter } from '@server/trpc/router';

import { useAuthStore } from '@/stores/auth.js';
import { useEnvironmentStore } from '@/stores/index.js';

// ============================================================================
// tRPC React Hooks
// ============================================================================

/** tRPC React Query hooks — use `trpc.{router}.{procedure}.useQuery()` etc. */
export const trpc = createTRPCReact<AppRouter>();

// ============================================================================
// Session Expiry Helpers
// ============================================================================

/**
 * Returns a promise that waits for the AbortSignal to fire.
 * When cancelQueries() is called, TanStack Query aborts the signal,
 * which rejects this promise and frees the query for a fresh fetch.
 *
 * @param { AbortSignal | undefined } signal The AbortSignal from TanStack Query.
 * @returns { Promise<Response> } Returns a promise that rejects on abort.
 */
const waitForAbort = (signal?: AbortSignal | null): Promise<Response> =>
  new Promise((_resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);

      return;
    }

    signal?.addEventListener('abort', () => reject(signal.reason), { once: true });
  });

// ============================================================================
// tRPC Client Factory
// ============================================================================

/**
 * Creates the tRPC vanilla client with httpBatchLink.
 * Includes credential forwarding and multi-tenant headers from the environment store.
 *
 * @returns { ReturnType<typeof trpc.createClient> } Returns the configured tRPC client.
 */
export const createTRpcClient = (): ReturnType<typeof trpc.createClient> =>
  trpc.createClient({
    links: [
      httpBatchLink({
        url: '/api/trpc',
        headers: () => {
          const headers: Record<string, string> = {
            'X-Requested-With': 'XMLHttpRequest',
          };

          const { currentEnvironment, currentRetailerCode } = useEnvironmentStore.getState();

          if (currentRetailerCode) {
            headers['X-Retailer-ID'] = currentRetailerCode;
          }

          if (currentEnvironment) {
            headers['X-Environment'] = currentEnvironment;
          }

          return headers;
        },
        fetch: async (url, options) => {
          if (useAuthStore.getState().sessionExpired) {
            return waitForAbort(options?.signal);
          }

          const response = await fetch(url, { ...options, credentials: 'include' });

          if (response.status === 401) {
            if (!useAuthStore.getState().sessionExpired) {
              useAuthStore.getState().markSessionExpired();
            }

            return waitForAbort(options?.signal);
          }

          return response;
        },
      }),
    ],
  });
